// Состояние ответа на один экран урока в предпросмотре-репетиции. Чистые
// данные и чистые операции: ничего не сохраняется и никуда не отправляется —
// это репетиция автора, а не прогресс ученика.
import type { Block } from '@/types/blocks'
import { exhaustive } from './exhaustive'

export type AnswerValue =
  | { kind: 'none' }
  /** Квиз с вариантами: `choice` (один/несколько) и `fill_blank` (одно слово). */
  | { kind: 'options'; selected: number[] }
  | { kind: 'yesNo'; value: boolean | null }
  /** Сопоставление — как в плеере (`useMatchGame`/`matchPairs`): правая
   *  колонка перемешана один раз и не двигается (`order[i]` — индекс пары,
   *  чья правая карточка стоит в строке `i`), а ответ — собранные тапами пары
   *  `pairs[левая] = правая` (индексы пар). */
  | { kind: 'match'; order: number[]; pairs: Record<number, number> }

export type AnswerKind = AnswerValue['kind']

/** Что спрашивает экран. Новый тип блока обязан сломать сборку здесь, а тип,
 *  которого фронт не знает, ведёт себя как контентный — «просто дальше». */
export const answerKind = (block: Block): AnswerKind => {
  switch (block.type) {
    case 'quiz':
      return block.quizType === 'yes_no' ? 'yesNo' : 'options'
    case 'match':
      return 'match'
    case 'info':
    case 'video':
    case 'article':
    case 'design':
      return 'none'
    default:
      return exhaustive(block, 'none')
  }
}

/**
 * Стартовый порядок правой колонки — сдвиг на единицу. Плеер её ПЕРЕМЕШИВАЕТ
 * (`useMatchGame` → `shuffle`), и авторская раскладка, оставленная как есть,
 * оказалась бы верной с первого нажатия: автор репетировал бы экран, которого
 * ученик никогда не увидит. Сдвиг даёт настоящее взаимодействие и при этом
 * детерминирован — случайность в предпросмотре не нужна. Одну пару сдвигать
 * некуда, порядок остаётся авторским.
 */
const shiftedOrder = (count: number): number[] =>
  Array.from({ length: count }, (_, i) => (count > 1 ? (i + 1) % count : i))

export const initialAnswer = (block: Block | null): AnswerValue => {
  if (!block) return { kind: 'none' }
  switch (answerKind(block)) {
    case 'options':
      return { kind: 'options', selected: [] }
    case 'yesNo':
      return { kind: 'yesNo', value: null }
    case 'match':
      return {
        kind: 'match',
        order: block.type === 'match' ? shiftedOrder(block.pairs.length) : [],
        pairs: {},
      }
    case 'none':
      return { kind: 'none' }
  }
}

/** Есть ли что проверять. Сопоставление — когда в паре каждый термин, как
 *  «Подтвердить» плеера (`isComplete`). */
export const isAnswered = (answer: AnswerValue): boolean => {
  switch (answer.kind) {
    case 'options':
      return answer.selected.length > 0
    case 'yesNo':
      return answer.value !== null
    case 'match':
      return answer.order.length > 0 && Object.keys(answer.pairs).length === answer.order.length
    case 'none':
      return true
  }
}

// Читатели: превью знает свой вид ответа, но не обязано его доказывать —
// на чужом виде отдаётся пустой дефолт, а не бросок.
export const selectedOptions = (answer: AnswerValue): number[] =>
  answer.kind === 'options' ? answer.selected : []

export const yesNoValue = (answer: AnswerValue): boolean | null =>
  answer.kind === 'yesNo' ? answer.value : null

export const matchOrder = (answer: AnswerValue): number[] =>
  answer.kind === 'match' ? answer.order : []

export const matchPairs = (answer: AnswerValue): Record<number, number> =>
  answer.kind === 'match' ? answer.pairs : {}

/** Клик по варианту: в множественном квизе копит, в одиночном — заменяет. */
export const toggleOption = (answer: AnswerValue, index: number, multi: boolean): AnswerValue => {
  if (answer.kind !== 'options') return answer
  if (!multi) return { kind: 'options', selected: [index] }
  const selected = answer.selected.includes(index)
    ? answer.selected.filter((i) => i !== index)
    : [...answer.selected, index].sort((a, b) => a - b)
  return { kind: 'options', selected }
}

/** Новые пары сопоставления (выбор карточек — `matchPick.ts`). */
export const setMatchPairs = (answer: AnswerValue, pairs: Record<number, number>): AnswerValue =>
  answer.kind === 'match' ? { ...answer, pairs } : answer
